import React, { useState, useEffect, useCallback } from "react";
import {
  getTierConfigValues,
  createTierConfigValue,
  deleteTierConfigValue,
} from "../../adminApi";
import type { TierConfigValue } from "../../adminTypes";

const TierConfigPanel: React.FC = () => {
  const [values, setValues] = useState<TierConfigValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchValues = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTierConfigValues();
      setValues(data);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load service line values");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchValues(); }, [fetchValues]);

  const handleAdd = async () => {
    setAddError("");
    if (!newValue || !newDescription.trim()) {
      setAddError("Both Service Line ID and description are required.");
      return;
    }
    setAddSaving(true);
    try {
      await createTierConfigValue({
        tier_id: 6,
        tier_value: parseInt(newValue),
        tier_value_description: newDescription.trim(),
      });
      showToast("success", "Value added.");
      setShowAddModal(false);
      setNewValue("");
      setNewDescription("");
      await fetchValues();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setAddError("That Service Line ID already exists.");
      } else {
        setAddError(err?.response?.data?.error || "Failed to add value.");
      }
    } finally {
      setAddSaving(false);
    }
  };

  const handleDeactivate = async (v: TierConfigValue) => {
    if (!window.confirm(`Deactivate "${v.tier_value_description}"?`)) return;
    try {
      await deleteTierConfigValue(v.id);
      showToast("success", `"${v.tier_value_description}" deactivated.`);
      await fetchValues();
    } catch (err: any) {
      showToast("error", err?.response?.data?.error || "Failed to deactivate.");
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const s = {
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" } as React.CSSProperties,
    title: { fontSize: "18px", fontWeight: 600, color: "#00386C", margin: 0 } as React.CSSProperties,
    subtitle: { fontSize: "14px", color: "#718096", margin: "0 0 20px 0" } as React.CSSProperties,
    addBtn: { backgroundColor: "#00386C", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 16px", fontSize: "14px", fontWeight: 500, cursor: "pointer" } as React.CSSProperties,
    tableWrap: { border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" } as React.CSSProperties,
    table: { width: "100%", borderCollapse: "collapse" as const, fontSize: "14px" },
    th: { textAlign: "left" as const, padding: "10px 16px", backgroundColor: "#f7fafc", borderBottom: "2px solid #e2e8f0", color: "#4a5568", fontWeight: 600, fontSize: "12px", textTransform: "uppercase" as const, letterSpacing: "0.05em" },
    tr: (active: boolean): React.CSSProperties => ({ backgroundColor: active ? "#fff" : "#fafafa", borderBottom: "1px solid #f0f0f0", opacity: active ? 1 : 0.55 }),
    td: { padding: "12px 16px", verticalAlign: "middle" as const } as React.CSSProperties,
    idBadge: { display: "inline-block", backgroundColor: "#e8f0ff", color: "#00386C", fontWeight: 700, borderRadius: "4px", padding: "2px 8px", fontSize: "13px" } as React.CSSProperties,
    activePill: { display: "inline-block", backgroundColor: "#e8f5e9", color: "#2d7a2d", borderRadius: "12px", padding: "2px 10px", fontSize: "12px", fontWeight: 600 } as React.CSSProperties,
    inactivePill: { display: "inline-block", backgroundColor: "#f5f5f5", color: "#9e9e9e", borderRadius: "12px", padding: "2px 10px", fontSize: "12px", fontWeight: 600 } as React.CSSProperties,
    deactivateBtn: { backgroundColor: "#fff0ee", color: "#c0392b", border: "1px solid #fca5a5", borderRadius: "4px", padding: "4px 12px", fontSize: "12px", cursor: "pointer" } as React.CSSProperties,
    // Modal
    overlay: { position: "fixed" as const, inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modal: { backgroundColor: "#fff", borderRadius: "10px", padding: "28px", width: "400px", maxWidth: "95vw", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" } as React.CSSProperties,
    modalTitle: { fontSize: "16px", fontWeight: 600, color: "#00386C", marginBottom: "20px", marginTop: 0 } as React.CSSProperties,
    label: { display: "block", fontSize: "12px", fontWeight: 600, color: "#4a5568", marginBottom: "4px", textTransform: "uppercase" as const, letterSpacing: "0.04em" } as React.CSSProperties,
    input: { width: "100%", padding: "8px 10px", border: "1px solid #cbd5e0", borderRadius: "5px", fontSize: "14px", boxSizing: "border-box" as const, marginBottom: "14px" },
    errorText: { color: "#c0392b", fontSize: "13px", marginBottom: "12px" } as React.CSSProperties,
    modalActions: { display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "8px" } as React.CSSProperties,
    cancelBtn: { backgroundColor: "#fff", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: "5px", padding: "8px 16px", fontSize: "14px", cursor: "pointer" } as React.CSSProperties,
    saveBtn: { backgroundColor: "#00386C", color: "#fff", border: "none", borderRadius: "5px", padding: "8px 20px", fontSize: "14px", cursor: "pointer", fontWeight: 500 } as React.CSSProperties,
    toastBase: { position: "fixed" as const, bottom: "24px", right: "24px", padding: "12px 20px", borderRadius: "8px", fontSize: "14px", fontWeight: 500, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
  };

  const toastStyle = (type: "success" | "error"): React.CSSProperties => ({
    ...s.toastBase,
    backgroundColor: type === "success" ? "#e8f5e9" : "#fdecea",
    color: type === "success" ? "#2d7a2d" : "#c0392b",
    border: `1px solid ${type === "success" ? "#c8e6c9" : "#ffcdd2"}`,
  });

  if (loading) return <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>Loading…</div>;
  if (error) return <div style={{ padding: "20px", color: "#c0392b" }}>{error}</div>;

  const activeCount = values.filter(v => v.active).length;

  return (
    <div>
      {toast && <div style={toastStyle(toast.type)}>{toast.message}</div>}

      <div style={s.headerRow}>
        <h2 style={s.title}>Service Line</h2>
        <button style={s.addBtn} onClick={() => { setShowAddModal(true); setAddError(""); setNewValue(""); setNewDescription(""); }}>
          + Add Value
        </button>
      </div>

      <p style={s.subtitle}>
        Maps WinTeam Tier 6 numeric values to human-readable service line names.
        {" "}{activeCount} active value{activeCount !== 1 ? "s" : ""}.
      </p>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Service Line ID</th>
              <th style={s.th}>Service Level Description</th>
              <th style={s.th}>Status</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {values.length === 0 && (
              <tr>
                <td colSpan={4} style={{ ...s.td, textAlign: "center", color: "#718096", fontStyle: "italic" }}>
                  No values defined yet.
                </td>
              </tr>
            )}
            {values.map((v) => (
              <tr key={v.id} style={s.tr(v.active)}>
                <td style={s.td}>
                  <span style={s.idBadge}>{v.tier_value}</span>
                </td>
                <td style={{ ...s.td, fontWeight: 500 }}>{v.tier_value_description}</td>
                <td style={s.td}>
                  {v.active
                    ? <span style={s.activePill}>Active</span>
                    : <span style={s.inactivePill}>Inactive</span>
                  }
                </td>
                <td style={{ ...s.td, textAlign: "right" }}>
                  {v.active && (
                    <button style={s.deactivateBtn} onClick={() => handleDeactivate(v)}>
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={s.modalTitle}>Add Service Level Description</h3>
            <label style={s.label}>Service Line ID <span style={{ color: "#c0392b" }}>*</span></label>
            <input
              style={s.input}
              type="number"
              placeholder="e.g. 108"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
            <label style={s.label}>Service Level Description <span style={{ color: "#c0392b" }}>*</span></label>
            <input
              style={s.input}
              placeholder="e.g. Dietary"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
            {addError && <div style={s.errorText}>{addError}</div>}
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setShowAddModal(false)} disabled={addSaving}>Cancel</button>
              <button style={s.saveBtn} onClick={handleAdd} disabled={addSaving}>
                {addSaving ? "Saving…" : "Add Value"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierConfigPanel;
